import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenantManagementService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import './ShipsReviewPage.css';

const APPROVAL_LABELS = {
  pending:  { text: 'قيد المراجعة', cls: 'badge--yellow' },
  approved: { text: 'مقبول',        cls: 'badge--green'  },
  rejected: { text: 'مرفوض',        cls: 'badge--red'    },
};

const STATUS_LABELS = {
  active:    { text: 'نشط',    cls: 'badge--green'  },
  suspended: { text: 'موقوف',  cls: 'badge--red'    },
  closed:    { text: 'مغلق',   cls: 'badge--gray'   },
};

const ShipsReviewPage = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [ships, setShips]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(true);
  const [filterStatus, setFilter]   = useState('');
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);

  const [rejectModal, setRejectModal]   = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approveModal, setApproveModal] = useState(null);
  const [approveNotes, setApproveNotes] = useState('');
  const [actioning, setActioning]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tenantManagementService.getAllShips({
        approval_status: filterStatus || undefined,
        search: search || undefined,
        page,
        page_size: 20,
      });
      setShips(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('فشل تحميل الأشياب');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, search, page]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async () => {
    if (!approveModal) return;
    setActioning(true);
    try {
      await tenantManagementService.approveShip(approveModal.tenant_id, approveModal.id, approveNotes);
      toast.success(`تم قبول الشيب "${approveModal.name}"`);
      setApproveModal(null);
      setApproveNotes('');
      load();
    } catch { toast.error('فشلت عملية القبول'); }
    finally { setActioning(false); }
  };

  const handleReject = async () => {
    if (!rejectModal || !rejectReason.trim()) {
      toast.error('يجب إدخال سبب الرفض');
      return;
    }
    setActioning(true);
    try {
      await tenantManagementService.rejectShip(rejectModal.tenant_id, rejectModal.id, rejectReason);
      toast.success(`تم رفض الشيب "${rejectModal.name}"`);
      setRejectModal(null);
      setRejectReason('');
      load();
    } catch { toast.error('فشلت عملية الرفض'); }
    finally { setActioning(false); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('ar-SA') : '—';
  const pageCount = Math.ceil(total / 20);

  return (
    <div className="srp">
      {/* Header */}
      <div className="srp__header">
        <div>
          <h1 className="srp__title">مراجعة الأشياب</h1>
          <p className="srp__sub">مراجعة وقبول/رفض أشياب الشركات • {total} نتيجة</p>
        </div>
      </div>

      {/* Filters */}
      <div className="srp__filters">
        <div className="srp__filter-tabs">
          {[
            { v: 'pending',  l: 'قيد المراجعة' },
            { v: 'approved', l: 'مقبول' },
            { v: 'rejected', l: 'مرفوض' },
            { v: '',         l: 'الكل' },
          ].map(({ v, l }) => (
            <button
              key={v}
              className={`srp__tab ${filterStatus === v ? 'srp__tab--active' : ''}`}
              onClick={() => { setFilter(v); setPage(1); }}
            >{l}</button>
          ))}
        </div>
        <input
          className="srp__search"
          placeholder="بحث باسم الشيب أو الشركة..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="srp__loading"><div className="srp__spinner" /></div>
      ) : ships.length === 0 ? (
        <div className="srp__empty">
          <span className="srp__empty-icon">🚢</span>
          <p>لا توجد أشياب في هذه الفئة</p>
        </div>
      ) : (
        <div className="srp__table-wrap">
          <table className="srp__table">
            <thead>
              <tr>
                <th>الشيب</th>
                <th>الشركة</th>
                <th>المدينة</th>
                <th>التاريخ</th>
                <th>الحالة التشغيلية</th>
                <th>حالة المراجعة</th>
                <th>الإجراء</th>
              </tr>
            </thead>
            <tbody>
              {ships.map(s => {
                const ap = APPROVAL_LABELS[s.approval_status] || APPROVAL_LABELS.pending;
                const st = STATUS_LABELS[s.status] || STATUS_LABELS.active;
                return (
                  <tr key={s.id}>
                    <td>
                      <div
                        className="srp__ship-cell srp__ship-cell--link"
                        onClick={() => navigate(`/admin/ships/${s.tenant_id}/${s.id}`)}
                        style={{cursor:'pointer'}}
                      >
                        <div className="srp__ship-avatar">{(s.name||'ش')[0]}</div>
                        <div>
                          <div className="srp__ship-name srp__link">{s.name}</div>
                          <div className="srp__ship-code">{s.ship_code || s.code}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <button
                        className="srp__tenant-link"
                        onClick={() => navigate(`/admin/tenants/${s.tenant_id}`)}
                      >{s.tenant_name}</button>
                    </td>
                    <td>{s.city || '—'}</td>
                    <td>{fmt(s.submitted_at || s.created_at)}</td>
                    <td><span className={`srp__badge ${st.cls}`}>{st.text}</span></td>
                    <td>
                      <span className={`srp__badge ${ap.cls}`}>{ap.text}</span>
                      {s.rejection_reason && (
                        <div className="srp__reason" title={s.rejection_reason}>
                          ⚠️ {s.rejection_reason.substring(0, 30)}{s.rejection_reason.length > 30 ? '...' : ''}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="srp__actions">
                        {s.approval_status !== 'approved' && (
                          <button
                            className="srp__btn srp__btn--approve"
                            onClick={() => { setApproveModal(s); setApproveNotes(''); }}
                          >✓ قبول</button>
                        )}
                        {s.approval_status !== 'rejected' && (
                          <button
                            className="srp__btn srp__btn--reject"
                            onClick={() => { setRejectModal(s); setRejectReason(''); }}
                          >✕ رفض</button>
                        )}
                        <button
                          className="srp__btn srp__btn--view"
                          onClick={() => navigate(`/admin/ships/${s.tenant_id}/${s.id}`)}
                        >تفاصيل</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="srp__pagination">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>السابق</button>
          <span>{page} / {pageCount}</span>
          <button disabled={page === pageCount} onClick={() => setPage(p => p + 1)}>التالي</button>
        </div>
      )}

      {/* Approve Modal */}
      {approveModal && (
        <div className="srp__overlay" onClick={() => setApproveModal(null)}>
          <div className="srp__modal" onClick={e => e.stopPropagation()}>
            <h3 className="srp__modal-title">قبول الشيب</h3>
            <p className="srp__modal-sub">الشيب: <strong>{approveModal.name}</strong> — {approveModal.tenant_name}</p>
            <div className="srp__field">
              <label>ملاحظات (اختياري)</label>
              <textarea
                value={approveNotes}
                onChange={e => setApproveNotes(e.target.value)}
                placeholder="أي ملاحظات للشركة..."
                rows={3}
              />
            </div>
            <div className="srp__modal-info">
              سيصبح الشيب نشطاً ومقبولاً ويمكنه استقبال الطلبات فوراً.
            </div>
            <div className="srp__modal-btns">
              <button className="srp__btn srp__btn--ghost" onClick={() => setApproveModal(null)}>إلغاء</button>
              <button className="srp__btn srp__btn--approve" onClick={handleApprove} disabled={actioning}>
                {actioning ? '...' : '✓ تأكيد القبول'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="srp__overlay" onClick={() => setRejectModal(null)}>
          <div className="srp__modal" onClick={e => e.stopPropagation()}>
            <h3 className="srp__modal-title">رفض الشيب</h3>
            <p className="srp__modal-sub">الشيب: <strong>{rejectModal.name}</strong> — {rejectModal.tenant_name}</p>
            <div className="srp__field">
              <label>سبب الرفض <span className="srp__req">*</span></label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="اذكر سبب الرفض بوضوح..."
                rows={3}
              />
            </div>
            <div className="srp__modal-info srp__modal-info--warn">
              سيتم إيقاف الشيب ولن يستقبل طلبات. يمكن قبوله لاحقاً.
            </div>
            <div className="srp__modal-btns">
              <button className="srp__btn srp__btn--ghost" onClick={() => setRejectModal(null)}>إلغاء</button>
              <button className="srp__btn srp__btn--reject" onClick={handleReject} disabled={actioning}>
                {actioning ? '...' : '✕ تأكيد الرفض'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipsReviewPage;
